import { ExternalLink } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../auth/AuthContext';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import type { PagedResult } from '../../../administration/users/userTypes';
import { SurveyPlanCreateModal } from '../../survey/components/SurveyPlanCreateModal';
import { SurveyPlanMembers } from '../../survey/components/SurveyPlanMembers';
import { useMemberNames } from '../../survey/components/useMemberNames';
import type { SurveyPlan } from '../../survey/surveyTypes';
import { DevelopmentPlanSurveyPickerModal } from '../components/DevelopmentPlanSurveyPickerModal';
import { developmentPlansApi } from '../developmentPlanApi';

/**
 * Learning & Development › Development Plan — every survey's PLANS, the same process as Survey ›
 * Development Plan (product call, 12 Oct 2026: the rows were the assessed employees).
 *
 * - **Create** asks which survey, then opens Survey's own two-step form: the plan, then the
 *   assessed employees it is for. It is Survey's write, so it carries `surveys.plan` — and, since
 *   both steps read Survey's lists, it is offered only with `surveys.read` too.
 * - **Title** is the row's anchor and opens the plan's profile: Info, and Personnel.
 * - **Personnel** is the employees it was created for, as small pictures — names resolved in ONE
 *   batch and never stored; above the reader's access level a member reads Restricted.
 *
 * An employee's OWN copy — its approval, sending, training, report and impact — is opened from the
 * plan's Personnel tab. Nothing here deletes: a plan has no delete, and a response's is Survey's.
 */
export function DevelopmentPlansIndexPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [rows, setRows] = useState<SurveyPlan[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  // Create: first the survey is picked, then Survey's own form opens on it.
  const [picking, setPicking] = useState(false);
  const [creatingFor, setCreatingFor] = useState<string | null>(null);

  const fetchRows = useCallback(async (query: IndexQueryState, signal: AbortSignal): Promise<PagedResult<SurveyPlan>> => {
    const page = await developmentPlansApi.listPlans(
      {
        search: query.search,
        page: query.page,
        pageSize: query.pageSize,
        sort: query.sort ?? undefined,
        status: query.facets.status?.join(','),
      },
      signal,
    );

    // The page on screen, for the ONE batch name resolve below.
    if (!signal.aborted) setRows(page.items);

    return page;
  }, []);

  // Every member on the page, for ONE batch resolve.
  const members = useMemo(() => rows.flatMap((row) => row.members), [rows]);
  const nameOf = useMemberNames(members);

  const columns = useMemo<ColumnDef<SurveyPlan>[]>(
    () => [
      {
        key: 'title',
        header: 'Title',
        width: 320,
        flex: true,
        sortable: true,
        render: (row) => <TextCell value={row.title} />,
      },
      {
        // Which survey the plan was created under — the one column Survey's own tab does not need.
        key: 'surveyTitle',
        header: 'Survey',
        width: 260,
        sortable: true,
        summary: true,
        render: (row) => <TextCell value={row.surveyTitle} />,
      },
      {
        // A SET, drawn as pictures — not sortable; the names are resolved for the page, never stored.
        key: 'members',
        header: 'Personnel',
        width: 260,
        summary: true,
        render: (row) => <SurveyPlanMembers members={row.members} nameOf={nameOf} />,
      },
      {
        // Counted live by the server. A zero is muted: no needs identified, not an absent value.
        key: 'needCount',
        header: 'Development Needs',
        width: 150,
        align: 'right',
        render: (row) => <TextCell value={String(row.needCount)} mono numeric />,
      },
      {
        key: 'status',
        header: 'Status',
        width: 120,
        status: true,
        render: (row) =>
          row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
      },
    ],
    [nameOf],
  );

  const rowActions = useCallback(
    (row: SurveyPlan): MenuItem[] => [
      {
        key: `survey-${row.id}`,
        label: 'Open survey',
        icon: <ExternalLink className="h-4 w-4" aria-hidden="true" />,
        permission: 'surveys.read',
        onClick: () => navigate(`/learning-development/survey/${row.surveyId}`),
      },
    ],
    [navigate],
  );

  return (
    <>
      <IndexPage<SurveyPlan>
        module="development-plans"
        breadcrumb={['Learning & Development', 'Development Plan']}
        columns={columns}
        facets={[{ key: 'status', label: 'Status' }]}
        // A plan, its needs, and what is written on each employee's own copy under it.
        auditEntityTypes={[
          'SurveyPlan',
          'SurveyPlanNeed',
          'SurveyRespondent',
          'SurveyDevelopmentNeed',
          'SurveyTrainingReportAttachment',
        ]}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search plans and surveys"
        emptyMessage="No development plan has been created yet."
        // Survey's write, behind Survey's lists — both keys, or the form would open onto a refusal.
        onCreate={hasPermission('surveys.read') ? () => setPicking(true) : undefined}
        createPermission="surveys.plan"
        // The TITLE opens the plan's profile — a page, so the breadcrumb carries the way back.
        onRowOpen={(row) => navigate(`/learning-development/development-plan/${row.id}`)}
        openPermission="development-plans.read"
        rowActions={rowActions}
        selectable
        otherInfo={(row) => row}
      />

      {picking ? (
        <DevelopmentPlanSurveyPickerModal
          onClose={() => setPicking(false)}
          onPicked={(surveyId) => {
            // One modal at a time: the picker closes as Survey's form opens on what it chose.
            setPicking(false);
            setCreatingFor(surveyId);
          }}
        />
      ) : null}

      {creatingFor !== null ? (
        <SurveyPlanCreateModal
          open
          surveyId={creatingFor}
          onClose={() => setCreatingFor(null)}
          onCreated={() => {
            setCreatingFor(null);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
    </>
  );
}
